import React, { useContext } from 'react'
import styled from 'styled-components'

import Accordion from '@jarvis.oddle/core/components/Accordion'
import { useAppContext } from 'src/containers/AppContext'
import { OptionHeader } from '@jarvis.oddle/core/molecules/ProductDetailsModal'
import {
  ContentContainer,
  ItemContainer
} from '@jarvis.oddle/core/molecules/ProductDetailsModal/ContentVariantElements'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import {
  marginSizes,
  buttonRadiusValues,
  typo
} from '@jarvis.oddle/core/theme/default'
import Form, { FormContext } from '@jarvis.oddle/core/components/Form'
import { status as productStatus } from '@jarvis.oddle/core/tools/product'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'

import OptionThumbnail from './OptionThumbnail'
import { formatDiff } from 'src/utils/number'
import {
  checkExistNutriGradeTag,
  NutriGradeIcon
} from 'src/components/icons/NutriGrade'
import { getAllProductNutrigradeTags } from 'src/utils/product'

interface ProductOptionProps {
  hideArrow: boolean
  subItemVariantsKey: string
  option: {
    id: string
    maximumQuantity: number
    minimumQuantity: number
    selectedQuantity: number
  }
  calculateOptionDiff: (optionId: string, variantId: string) => number
}

const OptionItemContainer = styled(ItemContainer).attrs(() => ({
  justifyContent: 'flex-start'
}))`
  && {
    height: auto;
  }
`

const WrappedOptionName = styled(Div)`
  position: relative;
`

const OptionTagContainer = styled.div`
  padding: 0;
  display: flex;
  flex-flow: wrap;
  grid-gap: 5px;
  margin-bottom: 10px;
`

const OptionVariantTag = styled.div`
  display: inline-block;
  background: white;
  padding: 4px 5px;
  border: 1px solid #bbb;
  color: black;
  font-family: 'Roboto', sans-serif;
  border-radius: ${(props) => buttonRadiusValues[props.theme.buttonShape]};
  font-size: ${typo.smallText.desktopFontSize};
`

const getOptionIdentifier = ({
  option,
  variant
}: {
  option: {
    id: string
    maximumQuantity: number
    minimumQuantity: number
    selectedQuantity: number
  }
  variant: {
    id: string
  }
}) => `subitems.${option.id}.${variant.id}`

const getQuantityFromFormValues = (values, name) => {
  const path = name.split('.')
  return path.reduce(
    (acc, cur) => (acc !== undefined ? acc[cur] : undefined),
    values
  )
}

const OptionContent = ({
  show = true,
  withInput = false,
  option,
  subItemVariantsKey = 'variants',
  renderImage,
  calculateOptionDiff
}: {
  show: boolean
  withInput: boolean
  option: {
    id: string
    maximumQuantity: number
    minimumQuantity: number
    selectedQuantity: number
  }
  subItemVariantsKey: string
  renderImage: (x) => void
  calculateOptionDiff: (optionId: string, variantId: string) => number
}) => {
  const { t } = useContext(TranslationContext)
  const { formatMoney } = useAppContext()
  const { maximumQuantity, selectedQuantity = 0 } = option
  const variants = option[subItemVariantsKey]
  const remainingQuantity = maximumQuantity - selectedQuantity
  const disableIncrease = remainingQuantity === 0

  const onQuantityChange = ({
    name,
    value: nextValue,
    values: prevValues,
    setFieldValue
  }) => {
    // check if there is an existing item and get the previous value
    const prevValue = name
      .split('.')
      .reduce((object, key) => object && object[key], prevValues)

    // if there is an existing item, check if the selectedQuantity plus the additionalQuantity is less than the maximum quantity
    // otherwise check if selected quantity plus the newly added quantity is less than the maximum quantity
    if (
      selectedQuantity + (prevValue ? nextValue - prevValue : nextValue) <=
      maximumQuantity
    ) {
      setFieldValue(name, nextValue)
    }
  }

  return (
    <ContentContainer show={show}>
      {(variants || []).map((variant, key) => {
        const diff = calculateOptionDiff(option.id, variant.id)
        const disabled =
          variant.status !== undefined &&
          variant.status !== productStatus.AVAILABLE
        const existNutriGradeTag = checkExistNutriGradeTag(variant.tags)
        const productNutrigradeTags = getAllProductNutrigradeTags(variant.tags)

        return (
          <OptionItemContainer
            key={key}
            disabled={disabled}
            last={key === variants.length - 1}
          >
            {renderImage({ variant })}
            <Flex flexDirection='column' alignItems='flex-start' flexGrow={1}>
              <WrappedOptionName>
                <H4
                  marginTop='none'
                  marginBottom={
                    existNutriGradeTag || productNutrigradeTags
                      ? marginSizes.extraSmall
                      : marginSizes.small
                  }
                >
                  {variant.variantName}
                </H4>
                {(existNutriGradeTag || productNutrigradeTags) && (
                  <Flex>
                    <NutriGradeIcon
                      tags={variant.tags}
                      height={40}
                      width={89}
                    />
                    {productNutrigradeTags && (
                      <Div marginLeft={marginSizes.extraSmall}>
                        <OptionTagContainer>
                          {productNutrigradeTags.map((tag) => (
                            <OptionVariantTag key={tag}>{tag}</OptionVariantTag>
                          ))}
                        </OptionTagContainer>
                      </Div>
                    )}
                  </Flex>
                )}
              </WrappedOptionName>
              <Flex
                alignSelf='stretch'
                alignItems='center'
                justifyContent='space-between'
                marginTop={
                  existNutriGradeTag || productNutrigradeTags
                    ? marginSizes.small
                    : marginSizes.none
                }
              >
                <Form.QuantitySelector
                  withInput={withInput}
                  onChange={(formProps) => onQuantityChange(formProps)}
                  disabled={disabled}
                  name={getOptionIdentifier({ option, variant })}
                  disableIncrease={disableIncrease}
                  getValue={getQuantityFromFormValues}
                  resetOnUnmount={false}
                />
                <H4
                  marginTop={marginSizes.extraSmall}
                  marginBottom={marginSizes.extraSmall}
                  data-testid={`text.priceDiff.${key}`}
                >
                  {disabled
                    ? t('Not available')
                    : diff > 0 &&
                      !disableIncrease &&
                      formatDiff({
                        value: diff,
                        formatter: formatMoney
                      })}
                </H4>
              </Flex>
            </Flex>
          </OptionItemContainer>
        )
      })}
    </ContentContainer>
  )
}

const ProductOption = ({
  hideArrow = false,
  subItemVariantsKey,
  option,
  calculateOptionDiff
}: ProductOptionProps): JSX.Element => {
  const { formatMoney } = useAppContext()
  return (
    <Accordion.Section
      id={option.id}
      data-testid={option.id}
      renderHeader={({ show }) => (
        <FormContext.Consumer>
          {({ status }) => (
            <OptionHeader
              show={show}
              option={option}
              hideArrow={hideArrow}
              touched={status === 'touched'}
              formatMoney={formatMoney}
            />
          )}
        </FormContext.Consumer>
      )}
      renderContent={({ show }) => (
        <OptionContent
          withInput
          subItemVariantsKey={subItemVariantsKey}
          option={option}
          show={show}
          calculateOptionDiff={calculateOptionDiff}
          renderImage={({ variant }) => <OptionThumbnail variant={variant} />}
        />
      )}
    />
  )
}

export default ProductOption
